import type {
  AdminPlatform,
  AdminProjectionFreshness,
  AdminQueryMeta,
  AdminReleaseRegression,
} from "../../contracts/admin-read-models";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import {
  buildAdminAiRuntimeSummaryViewModel,
  type AdminAiRuntimeSummaryViewModel,
} from "../../shared/locale/admin-ai-runtime-summary";
import {
  buildAdminObservabilityAggregateViewModel,
  type AdminObservabilityAggregateViewModel,
} from "../../shared/locale/admin-observability-aggregate-summary";
import {
  buildAdminReleaseRowSnapshotViewModel,
  type AdminReleaseRowSnapshotViewModel,
} from "../../shared/locale/admin-release-row-snapshot-summary";
import { formatAdminCount } from "../../shared/formatting/format-admin-count";
import { formatAdminDate } from "../../shared/formatting/format-admin-date";
import { formatAdminSourceKindLabel } from "../../shared/formatting/format-admin-source-kind";
import { findReleaseRegressionHotspots, type AdminReleaseHotspot } from "./releases-use-cases";

export type ReleaseRegressionHotspotViewModel = {
  release: string;
  appVersionLabel: string;
  platformLabel: string;
  topErrorCodeLabel: string;
  headline: string;
  summary: string;
  drilldownTo: string;
};

export type ReleaseRegressionViewModel = {
  aiRuntime?: AdminAiRuntimeSummaryViewModel;
  observability?: AdminObservabilityAggregateViewModel;
  rowSnapshot?: AdminReleaseRowSnapshotViewModel;
  generatedAtLabel: string;
  requestId: string;
  nextCursor?: string;
  freshness: AdminProjectionFreshness;
  sourceFreshness: Array<{
    source: string;
    freshness: AdminProjectionFreshness;
    sourceKind?: string;
    sourceKindLabel?: string;
  }>;
  hotspots: ReleaseRegressionHotspotViewModel[];
  rows: Array<{
    release: string;
    deployedAt?: string;
    requestCount: number;
    errorRate: number;
    jobFailureDelta?: number;
    dominantPlatformRatio?: number;
    appVersionLabel: string;
    deployedAtLabel: string;
    requestCountLabel: string;
    errorRateLabel: string;
    topFailedUseCaseLabel: string;
    topErrorCodeLabel: string;
    jobFailureDeltaLabel: string;
    dominantPlatformLabel: string;
    dominantPlatformRatioLabel: string;
    drilldownTo: string;
  }>;
};

function toFreshness(meta: AdminQueryMeta): AdminProjectionFreshness {
  return Object.values(meta.sourceFreshness).includes("stale")
    ? "stale"
    : Object.values(meta.sourceFreshness).includes("partial") ||
        Object.values(meta.sourceFreshness).includes("unavailable")
      ? "partial"
      : "fresh";
}

function buildSourceFreshness(
  meta: AdminQueryMeta,
  locale: AdminDashboardLocale,
): ReleaseRegressionViewModel["sourceFreshness"] {
  return Object.entries(meta.sourceFreshness).map(([source, freshness]) => ({
    source,
    freshness,
    sourceKind: meta.sourceKinds?.[source],
    sourceKindLabel: formatAdminSourceKindLabel(meta.sourceKinds?.[source], locale),
  }));
}

function toLabel(value: string | undefined): string {
  return value ?? "-";
}

function text(locale: AdminDashboardLocale, en: string, ko: string): string {
  return pickAdminDashboardLocaleText(locale, { en, ko });
}

function formatErrorRate(value: number): string {
  return `${(value * 100).toFixed(1)}%`;
}

function findDominantPlatform(
  concentration: Partial<Record<AdminPlatform, number>> | undefined,
): [string, string] {
  if (!concentration) {
    return ["-", "-"];
  }

  const entries = Object.entries(concentration) as Array<[AdminPlatform, number]>;
  entries.sort((left, right) => right[1] - left[1]);
  const [platform, ratio] = entries[0] ?? ["unknown", 0];
  return [platform.toUpperCase(), `${(ratio * 100).toFixed(1)}%`];
}

function buildHotspotHeadline(
  hotspot: AdminReleaseHotspot,
  locale: AdminDashboardLocale,
): string {
  if (hotspot.topErrorCode) {
    return locale === "en"
      ? `${hotspot.topErrorCode} hotspot`
      : `${hotspot.topErrorCode} 핫스팟`;
  }

  if (hotspot.platform) {
    return locale === "en"
      ? `${hotspot.platform.toUpperCase()} release hotspot`
      : `${hotspot.platform.toUpperCase()} 릴리스 핫스팟`;
  }

  return text(locale, "Release hotspot", "릴리스 핫스팟");
}

function buildHotspotSummary(
  item: AdminReleaseRegression["items"][number],
  hotspot: AdminReleaseHotspot,
  locale: AdminDashboardLocale,
): string {
  const requestCountLabel = formatAdminCount(item.requestCount);
  const errorRateLabel = formatErrorRate(item.errorRate);
  const platformLabel = hotspot.platform ? hotspot.platform.toUpperCase() : undefined;

  if (hotspot.topErrorCode && platformLabel) {
    return locale === "en"
      ? `${hotspot.topErrorCode} is leading on ${platformLabel} for ${errorRateLabel} of ${requestCountLabel} requests.`
      : `${platformLabel}에서 ${hotspot.topErrorCode}가 ${requestCountLabel}건 중 ${errorRateLabel} 비율로 가장 많이 나타납니다.`;
  }

  if (hotspot.topErrorCode) {
    return locale === "en"
      ? `${hotspot.topErrorCode} is leading for ${errorRateLabel} of ${requestCountLabel} requests in this release bucket.`
      : `이 릴리스 버킷에서는 ${hotspot.topErrorCode}가 ${requestCountLabel}건 중 ${errorRateLabel} 비율로 가장 많이 나타납니다.`;
  }

  if (item.topFailedUseCase) {
    return locale === "en"
      ? `${item.topFailedUseCase} is the busiest failed use case in this release bucket.`
      : `${item.topFailedUseCase} 유스케이스가 이 릴리스 버킷에서 가장 많이 실패합니다.`;
  }

  return locale === "en"
    ? "Open the failed-flow bucket to confirm whether this spike is isolated or part of a wider regression."
    : "오류 흐름 버킷을 열어 이 급증이 단일 이슈인지 더 넓은 회귀인지 확인하세요.";
}

export function toReleaseRegressionViewModel(
  regression: AdminReleaseRegression,
  meta: AdminQueryMeta,
  locale: AdminDashboardLocale = "ko",
): ReleaseRegressionViewModel {
  const hotspots = findReleaseRegressionHotspots(regression);

  return {
    aiRuntime: buildAdminAiRuntimeSummaryViewModel(regression.aiRuntimeOperations, locale, [
      "aiProviderRouteLabel",
      "aiServingAccessState",
      "aiBudgetThresholdState",
      "aiModelUsageLineage",
      "aiModelUsageSummary",
      "aiModelLimitSummary",
    ]),
    observability: buildAdminObservabilityAggregateViewModel(regression.observabilityAggregate, locale, [
      "releaseErrorSpikeSummary",
      "businessFlowCompletionRate",
    ]),
    rowSnapshot: buildAdminReleaseRowSnapshotViewModel(regression.items, locale),
    generatedAtLabel: formatAdminDate(meta.generatedAt),
    requestId: meta.requestId,
    nextCursor: meta.nextCursor,
    freshness: toFreshness(meta),
    sourceFreshness: buildSourceFreshness(meta, locale),
    hotspots: regression.items.map((item, index) => {
      const hotspot = hotspots[index];
      const [platformLabel] = findDominantPlatform(item.platformConcentration);

      return {
        release: item.release,
        appVersionLabel: toLabel(item.appVersion),
        platformLabel: hotspot?.platform?.toUpperCase() ?? platformLabel,
        topErrorCodeLabel: toLabel(item.topErrorCode),
        headline: buildHotspotHeadline(hotspot ?? { release: item.release, drilldownTo: "/admin/failed-flows" }, locale),
        summary: buildHotspotSummary(
          item,
          hotspot ?? { release: item.release, drilldownTo: "/admin/failed-flows" },
          locale,
        ),
        drilldownTo: hotspot?.drilldownTo ?? "/admin/failed-flows",
      };
    }),
    rows: regression.items.map((item, index) => {
      const [dominantPlatformLabel, dominantPlatformRatioLabel] = findDominantPlatform(
        item.platformConcentration,
      );

      return {
        release: item.release,
        deployedAt: item.deployedAt,
        requestCount: item.requestCount,
        errorRate: item.errorRate,
        jobFailureDelta: item.jobFailureDelta,
        dominantPlatformRatio: Object.values(item.platformConcentration ?? {}).sort((left, right) => right - left)[0],
        appVersionLabel: toLabel(item.appVersion),
        deployedAtLabel: formatAdminDate(item.deployedAt),
        requestCountLabel: formatAdminCount(item.requestCount),
        errorRateLabel: formatErrorRate(item.errorRate),
        topFailedUseCaseLabel: toLabel(item.topFailedUseCase),
        topErrorCodeLabel: toLabel(item.topErrorCode),
        jobFailureDeltaLabel:
          item.jobFailureDelta === undefined ? "-" : formatAdminCount(item.jobFailureDelta),
        dominantPlatformLabel,
        dominantPlatformRatioLabel,
        drilldownTo: hotspots[index]?.drilldownTo ?? "/admin/failed-flows",
      };
    }),
  };
}
